import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useTaskSchema } from "components/Tasks/createTaskSchema";
import { TASKS_COUNT_QUERY } from "components/Tasks/schema";
import { TaskDialog } from "components/Tasks/TaskDialog";
import { TaskFormType } from "components/Tasks/types";
import { gql } from "generated-graphql";
import { Frequency, TaskStatus } from "generated-graphql/graphql";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useTenant } from "hooks/useTenant";
import invariant from "invariant";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";

const CREATE_TASK_MUTATION = gql(/* GraphQL */ `
  mutation CreateTask($input: CreateTaskInput!) {
    createTask(input: $input)
  }
`);

const defaultValues: TaskFormType = {
  title: "",
  description: "",
  assignee: null,
  deadline: null,
  frequency: Frequency.Once,
  taskStatus: TaskStatus.Open,
  interval: 1,
  reminderDays: [],
  subtasks: [],
  facilityIds: [],
  tags: [],
  documents: [],
  links: [],
  watchers: [],
  completedOn: null,
  completionTiming: null,
  completionDeltaDays: null,
  isSuggested: false,
};

type AddTaskDialogProps = {
  open: boolean;
  onClose: () => void;
  initialFacilityIds?: string[];
};

export const AddTaskDialog: React.FC<AddTaskDialogProps> = ({
  open,
  onClose,
  initialFacilityIds,
}) => {
  const alerts = useAlerts();
  const { tenantId, loading: isTenantLoading } = useTenant();
  const yupSchema = useTaskSchema();

  const initialValues: TaskFormType = useMemo(
    () => ({ ...defaultValues, facilityIds: initialFacilityIds ?? [] }),
    [initialFacilityIds]
  );

  const [createTask, { loading: isCreateTaskLoading }] = useMutation(CREATE_TASK_MUTATION, {
    refetchQueries: ["Tasks", TASKS_COUNT_QUERY],
  });

  const [deleteDocument] = useDeleteDocumentMutation({
    onError: () => {
      alerts.error("Failed to delete document");
    },
  });

  const handleClose = useCallback(
    (formValues: TaskFormType) => {
      // delete any documents that we may have created, as they were not meant to be persisted
      if (formValues.documents) {
        formValues.documents.forEach((document) => {
          if (document.id) {
            deleteDocument({ variables: { id: document.id } });
          }
        });
      }

      onClose();
    },
    [deleteDocument, onClose]
  );

  const handleSave = useCallback(
    (data: TaskFormType) => {
      invariant(tenantId, "tenantId is required");

      createTask({
        variables: {
          input: {
            tenantId,
            title: data.title,
            description: data.description,
            assigneeId: data.assignee?.id,
            deadline: data.deadline ? new Date(data.deadline).toISOString().slice(0, 10) : null,
            reminderDays: data.reminderDays.map((reminder) => reminder.days),
            frequency: data.frequency,
            interval: data.interval,
            watchers: data.watchers.map((watcher) => watcher.id),
            facilityIds: data.facilityIds,
            documentIds: data.documents.map((document) => document.id),
            links: data.links.map((link) => link.url),
            subtasks: data.subtasks
              .filter((subTask) => subTask.title?.trim() !== "")
              .map((subTask) => ({
                title: subTask.title,
                completedAt: subTask.completed ? DateTime.now() : null,
              })),
            tags: data.tags.filter((tag) => tag.name.trim() !== ""),
          },
        },
        onCompleted: () => {
          alerts.success("Task created");
          onClose();
        },
        onError: () => {
          alerts.error("Failed to create task");
          onClose();
        },
      });
    },
    [tenantId, createTask, alerts, onClose]
  );

  return (
    <TaskDialog
      isLoading={isTenantLoading}
      initialValues={initialValues}
      validationResolver={yupResolver(yupSchema) as any}
      onSubmit={handleSave}
      onClose={handleClose}
      isSubmitting={isCreateTaskLoading}
      open={open}
      cancelButtonText="Cancel"
      submitButtonText="Create Task"
      showCommentsSection={false}
      autoFocusTitle
      disableRestoreFocus
    />
  );
};
